import { useState, useEffect } from "react";
import { Search, Filter, ChevronLeft, ChevronRight, UserCheck, Trash2, RefreshCw } from "lucide-react";
import { api } from "../api/client";
import type { Recipient } from "../types";
import { DUNS } from "../types";

export default function Recipients() {
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    institution: "",
    dun: "",
    slotKod: "",
    age: "",
    status: "",
  });
  const [selectedRecipient, setSelectedRecipient] = useState<Recipient | null>(null);
  const [isCheckingIn, setIsCheckingIn] = useState(false);
  const [checkInMessage, setCheckInMessage] = useState("");

  const limit = 20;

  useEffect(() => {
    fetchRecipients();
  }, [page, filters]);

  const fetchRecipients = async () => {
    setIsLoading(true);
    try {
      const data = await api.getRecipients({
        page,
        limit,
        search: search || undefined,
        institution: filters.institution || undefined,
        dun: filters.dun || undefined,
        slotKod: filters.slotKod || undefined,
        age: filters.age || undefined,
        status: filters.status || undefined,
      });
      setRecipients(data.recipients);
      setTotal(data.total);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchRecipients();
  };

  const handleCheckIn = async (recipient: Recipient) => {
    setSelectedRecipient(recipient);
    setCheckInMessage("");
  };

  const handleUndoCheckInRow = async (recipient: Recipient) => {
    if (!confirm("Adakah anda pasti ingin membatalkan daftar?")) return;
    
    try {
      const result = await api.undoCheckIn(recipient.id);
      if (result.success) {
        setCheckInMessage("✓ Berjaya Batal Daftar!");
        fetchRecipients();
      } else {
        alert(result.message || "Ralat berlaku");
      }
    } catch (err: any) {
      alert(err.message || "Ralat berlaku");
    }
  };

  const confirmCheckIn = async () => {
    if (!selectedRecipient) return;

    setIsCheckingIn(true);
    try {
      const result = await api.checkIn(selectedRecipient.id);
      if (result.success) {
        setCheckInMessage("✓ Berjaya Daftar Masuk!");
        setTimeout(() => {
          setSelectedRecipient(null);
          fetchRecipients();
        }, 1500);
      } else {
        setCheckInMessage(result.message || "Ralat berlaku");
      }
    } catch (err: any) {
      setCheckInMessage(err.message || "Ralat berlaku");
    } finally {
      setIsCheckingIn(false);
    }
  };

  const confirmUndoCheckIn = async () => {
    if (!selectedRecipient) return;

    setIsCheckingIn(true);
    try {
      const result = await api.undoCheckIn(selectedRecipient.id);
      if (result.success) {
        setCheckInMessage("✓ Berjaya Batal Daftar!");
        setTimeout(() => {
          setSelectedRecipient(null);
          fetchRecipients();
        }, 1500);
      } else {
        setCheckInMessage(result.message || "Ralat berlaku");
      }
    } catch (err: any) {
      setCheckInMessage(err.message || "Ralat berlaku");
    } finally {
      setIsCheckingIn(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Adakah anda pasti ingin padam penerima ini?")) return;

    try {
      const result = await api.deleteRecipient(id);
      if (result.success) {
        fetchRecipients();
      } else {
        alert(result.message);
      }
    } catch (err) {
      alert("Ralat berlaku");
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Senarai Penerima</h1>
        <p className="text-slate-600">Urus dan semak penerima bantuan</p>
      </div>

      {/* Search & Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <form onSubmit={handleSearch} className="flex gap-3 mb-4">
          <div className="flex-1 relative">
            <Search className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cari nama, no. IC, Bil, atau no. telefon..."
              className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Cari
          </button>
          <button
            type="button"
            onClick={() => {
              setSearch("");
              setFilters({ institution: "", dun: "", slotKod: "", age: "", status: "" });
              setPage(1);
            }}
            className="px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50"
          >
            Reset
          </button>
        </form>

        <div className="flex flex-wrap gap-3">
          <input
            type="text"
            value={filters.institution}
            onChange={(e) => {
              setFilters({ ...filters, institution: e.target.value });
              setPage(1);
            }}
            placeholder="Filter Institusi / Universiti"
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm min-w-56"
          />

          <select
            value={filters.dun}
            onChange={(e) => {
              setFilters({ ...filters, dun: e.target.value });
              setPage(1);
            }}
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
          >
            <option value="">Semua DUN</option>
            {DUNS.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>

          <input
            type="text"
            value={filters.slotKod}
            onChange={(e) => {
              setFilters({ ...filters, slotKod: e.target.value });
              setPage(1);
            }}
            placeholder="Filter Slot Kod"
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm min-w-40"
          />

          <input
            type="number"
            min="0"
            value={filters.age}
            onChange={(e) => {
              setFilters({ ...filters, age: e.target.value });
              setPage(1);
            }}
            placeholder="Filter Umur (cth: 40)"
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm min-w-40"
          />

          <select
            value={filters.status}
            onChange={(e) => {
              setFilters({ ...filters, status: e.target.value });
              setPage(1);
            }}
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
          >
            <option value="">Semua Status</option>
            <option value="0">BELUM HADIR</option>
            <option value="1">HADIR</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : recipients.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            Tiada penerima dijumpai
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Bil</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Slot Kod</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Nama</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">No. IC</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Institusi</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Jurusan</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Umur</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Tindakan</th>
                  </tr>
                </thead>
                <tbody>
                  {recipients.map((r) => (
                    <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4 font-mono text-sm">{r.bil}</td>
                      <td className="py-3 px-4 text-sm font-mono">{r.slotKod || "-"}</td>
                      <td className="py-3 px-4">{r.nama}</td>
                      <td className="py-3 px-4 font-mono text-sm">{r.noIc}</td>
                      <td className="py-3 px-4 text-sm">{r.institution}</td>
                      <td className="py-3 px-4 text-sm">{r.jurusan || "-"}</td>
                      <td className="py-3 px-4 text-sm">{r.age ?? "-"}</td>
                      <td className="py-3 px-4">
                        {r.statusKehadiran === 1 ? (
                          r.wakilNama ? (
                            <span className="inline-flex items-center gap-1 text-amber-600 text-sm">
                              <UserCheck className="w-4 h-4" />
                              WAKIL
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-green-600 text-sm">
                              <UserCheck className="w-4 h-4" />
                              SENDIRI
                            </span>
                          )
                        ) : (
                          <span className="text-slate-500 text-sm">Belum</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2">
                          {r.statusKehadiran === 0 && (
                            <button
                              onClick={() => handleCheckIn(r)}
                              className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                            >
                              Daftar
                            </button>
                          )}
                          {r.statusKehadiran === 1 && (
                            <button
                              onClick={() => handleUndoCheckInRow(r)}
                              className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                            >
                              Batal
                            </button>
                          )}
                          {r.statusKehadiran === 0 && (
                            <button
                              onClick={() => handleDelete(r.id)}
                              className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
                <p className="text-sm text-slate-500">
                  Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1 border border-slate-300 rounded text-sm disabled:opacity-50"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="px-3 py-1 text-sm">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-3 py-1 border border-slate-300 rounded text-sm disabled:opacity-50"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Check-in Modal */}
      {selectedRecipient && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h2 className="text-xl font-bold text-slate-800 mb-4">
              {selectedRecipient.statusKehadiran === 1 ? "Batal Daftar" : "Daftar Masuk"} Penerima
            </h2>
            
            <div className="bg-slate-50 rounded-lg p-4 mb-4">
              <p className="font-medium">{selectedRecipient.nama}</p>
              <p className="text-sm text-slate-600 font-mono">{selectedRecipient.noIc}</p>
              <p className="text-sm text-slate-600">{selectedRecipient.institution} - {selectedRecipient.dun}</p>
              <p className="text-sm text-slate-600">Slot Kod: {selectedRecipient.slotKod || "-"}</p>
              <p className="text-sm text-slate-600">Slot Masa: {selectedRecipient.slotMasa || "-"}</p>
              <p className="text-sm text-slate-600">Tarikh Slot: {selectedRecipient.tarikhSlot || "-"}</p>
              <p className="text-sm text-blue-600">Umur: {selectedRecipient.age ?? "-"}</p>
              {selectedRecipient.statusKehadiran === 1 && (
                <p className={`text-sm mt-2 ${selectedRecipient.wakilNama ? "text-amber-600" : "text-green-600"}`}>
                  ✓ {selectedRecipient.wakilNama ? `Hadir sebagai WAKIL (${selectedRecipient.wakilHubungan})` : "Hadir SENDIRI"}
                </p>
              )}
            </div>

            {/* WAKIL Details in Modal */}
            {selectedRecipient.wakilNama && (
              <div className="bg-amber-50 rounded-lg p-4 mb-4 border border-amber-200">
                <p className="font-semibold text-amber-800 mb-2">Maklumat WAKIL:</p>
                <p className="text-sm"><span className="text-slate-500">Nama:</span> {selectedRecipient.wakilNama}</p>
                <p className="text-sm"><span className="text-slate-500">No. IC:</span> {selectedRecipient.wakilNoIc}</p>
                {selectedRecipient.wakilNoTel && <p className="text-sm"><span className="text-slate-500">No. Tel:</span> {selectedRecipient.wakilNoTel}</p>}
                {selectedRecipient.wakilAlamat && <p className="text-sm"><span className="text-slate-500">Alamat:</span> {selectedRecipient.wakilAlamat}</p>}
                <p className="text-sm"><span className="text-slate-500">Hubungan:</span> {selectedRecipient.wakilHubungan}</p>
              </div>
            )}

            {checkInMessage && (
              <div className={`p-3 rounded-lg mb-4 ${checkInMessage.includes("✓") ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
                {checkInMessage}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setSelectedRecipient(null)}
                className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-slate-700"
              >
                Batal
              </button>
              {selectedRecipient.statusKehadiran === 0 ? (
                <button
                  onClick={confirmCheckIn}
                  disabled={isCheckingIn}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {isCheckingIn ? "Memproses..." : "Daftar Masuk"}
                </button>
              ) : (
                <button
                  onClick={confirmUndoCheckIn}
                  disabled={isCheckingIn}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {isCheckingIn ? "Memproses..." : "Batal Daftar"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
